import apiClient from './apiClient';
import { User } from '../types';

/**
 * Teacher Service
 * Provides API calls for teacher-specific functionality
 * 
 * Note: This is a simplified implementation that works with the current user-based system.
 * In a full implementation, this would include proper class/student management.
 */

export interface TeacherStudent extends User {
  // For now, we'll use a simplified approach where teachers can see all students
  // In a full implementation, this would include class assignment
}

export interface TeacherClass {
  id: string;
  name: string;
  teacherId: string;
  studentIds: string[];
}

export interface StudentPerformanceData {
  userId: number;
  quizAttempts: number;
  averageScore: number;
  totalQuestions: number;
  correctAnswers: number;
  subjectBreakdown: {
    [subject: string]: {
      attempts: number;
      averageScore: number;
    };
  };
  recentAttempts: any[]; // QuizAttempt records
}

/**
 * Get all students (simplified - in real app would be class-based)
 * @returns Promise<TeacherStudent[]>
 */
export const getStudents = async (): Promise<TeacherStudent[]> => {
  try {
    const response = await apiClient.get<User[]>('/users?role=student');
    return response.data;
  } catch (error) {
    console.error('Error fetching students:', error);
    throw error;
  }
};

/**
 * Get all teachers 
 * @returns Promise<User[]>
 */
export const getTeachers = async (): Promise<User[]> => {
  try {
    const response = await apiClient.get<User[]>('/users?role=teacher');
    return response.data;
  } catch (error) {
    console.error('Error fetching teachers:', error);
    throw error;
  }
};

/**
 * Get performance data for a specific student
 * @param studentId - ID of the student
 * @returns Promise<StudentPerformanceData>
 */
export const getStudentPerformance = async (studentId: number): Promise<StudentPerformanceData> => {
  try {
    const response = await apiClient.get<StudentPerformanceData>(`/users/${studentId}/performance`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching performance for student ${studentId}:`, error);
    // Return empty performance data if API not implemented yet
    return {
      userId: studentId,
      quizAttempts: 0,
      averageScore: 0,
      totalQuestions: 0,
      correctAnswers: 0,
      subjectBreakdown: {},
      recentAttempts: []
    };
  }
};

/**
 * Get quiz attempts for a specific student
 * @param studentId - ID of the student  
 * @returns Promise<any[]>
 */
export const getStudentQuizAttempts = async (studentId: number): Promise<any[]> => {
  try {
    const response = await apiClient.get<any[]>(`/quiz-attempts/user/${studentId}/all`);
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching quiz attempts for student ${studentId}:`, error);
    return [];
  }
};

// Simplified class management - returns a single "class" for now
export const getTeacherClasses = async (teacherId: number): Promise<TeacherClass[]> => {
  try {
    // For now, return a simple class containing all students
    const students = await getStudents();
    return [{
      id: 'all-students',
      name: 'All Students',
      teacherId: teacherId.toString(),
      studentIds: students.map(s => s.userId.toString())
    }];
  } catch (error) {
    console.error('Error fetching teacher classes:', error);
    return [];
  }
};